import { useTranslation, useAuth } from '@/context/AuthContext';
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTrip, addTraveler, removeTraveler, updateTraveler, addBooking, toggleLockBooking, importBookings, addCompliance, updateCompliance, addExpense, createTask, updateTask, inviteTraveler } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Plus, Upload, Lock, Unlock, Trash2, Users, Plane, ClipboardList, ShieldCheck, Wallet, Calendar, Sparkles, Activity, AlertTriangle, Heart, BookOpen, Compass, Mic, Link2, MapPin, LayoutDashboard, Mail, ChevronRight, Clock, Pencil, Edit3 } from 'lucide-react';
import { DayPlanner } from '@/pages/DayPlanner';
import { AICopilot } from '@/pages/AICopilot';
import { IncidentMode } from '@/pages/IncidentMode';
import { BudgetCharts } from '@/pages/BudgetCharts';
import { ShareLinks } from '@/pages/ShareLinks';
import { HealthModule } from '@/pages/HealthModule';
import { JournalGallery } from '@/pages/JournalGallery';
import { VisitCompanion } from '@/pages/VisitCompanion';
import { VoiceCopilot } from '@/pages/VoiceCopilot';
import { TripMap } from '@/pages/TripMap';
import { toast } from 'sonner';
import { useRole } from '@/context/RoleContext';
import ModeSelector from '@/components/ModeSelector';
import InviteTravelerModal from '@/components/InviteTravelerModal';
import { QuickDeckFAB } from '@/components/quickdeck/QuickDeckFAB';
import ScanHereModal from '@/components/ScanHereModal';

export default function TripDetail() {
  const { tripId } = useParams();
  const { t } = useTranslation();
  const { families } = useAuth();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [scanHereOpen, setScanHereOpen] = useState(false);
  
  // Get user role and permissions from context
  const { role, permissions, canSwitchMode, switchMode, loading: roleLoading, uiMode } = useRole();

  const loadTrip = useCallback(async () => {
    try {
      const res = await getTrip(tripId);
      setTrip(res.data);
    } catch { toast.error('Failed to load trip'); }
    setLoading(false);
  }, [tripId]);

  useEffect(() => { loadTrip(); }, [loadTrip]);

  if (loading || !trip) {
    return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-[#E2E8F0] rounded-2xl animate-pulse" />)}</div>;
  }

  const daysUntil = trip.start_date ? Math.ceil((new Date(trip.start_date) - new Date()) / 86400000) : null;
  const tasksDone = trip.tasks?.filter(t => t.status === 'done').length || 0;
  const tasksTotal = trip.tasks?.length || 0;
  const compOk = trip.compliance?.filter(c => c.status === 'valid' || c.status === 'not_required').length || 0;
  const compTotal = trip.compliance?.length || 0;
  const readiness = tasksTotal + compTotal > 0 ? Math.round(((tasksDone + compOk) / (tasksTotal + compTotal)) * 100) : 0;
  
  // Permission checks
  const canEdit = permissions?.can_edit_trip ?? false;
  const canManageBudget = permissions?.can_manage_budget ?? false;
  const canViewBudgetDetails = permissions?.can_view_budget_details ?? false;
  const isKidsMode = uiMode === 'kids';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="trip-detail-page">
      {/* Header with Mode Selector */}
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/trips')} className="h-9 w-9 p-0 rounded-lg mt-1" data-testid="back-to-trips">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl md:text-3xl text-[#1E293B]" >{trip.name}</h1>
          <p className="text-sm text-[#64748B] mt-0.5">
            {trip.destination && `${trip.destination} · `}
            {trip.start_date && new Date(trip.start_date).toLocaleDateString()} - {trip.end_date && new Date(trip.end_date).toLocaleDateString()}
          </p>
        </div>
        
        {/* Mode Selector for organizers */}
        {!roleLoading && canSwitchMode && (
          <ModeSelector currentRole={role} canSwitch={canSwitchMode} onSwitchMode={switchMode} />
        )}
        
        {daysUntil !== null && daysUntil > 0 && (
          <Badge className="rounded-full bg-[#3A86FF]/10 text-[#3A86FF] border-0 text-sm px-4 py-1">
            <Calendar className="w-3.5 h-3.5 mr-1" /> {daysUntil} {t('tripDetail.daysUntil')}
          </Badge>
        )}
      </div>

      {/* Readiness bar */}
      {!isKidsMode && (
        <Card className="border border-[#E2E8F0]/40 rounded-2xl">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-[#1E293B]">{t('tripDetail.readiness')}</span>
              <span className="text-2xl font-bold text-[#3A86FF]">{readiness}%</span>
            </div>
            <Progress value={readiness} className="h-2" />
            <div className="flex gap-6 mt-3 text-xs text-[#64748B]">
              <span>{t('tripDetail.tasks')}: {tasksDone}/{tasksTotal}</span>
              <span>{t('tripDetail.compliance')}: {compOk}/{compTotal}</span>
              <span>{t('tripDetail.bookings')}: {trip.bookings?.length || 0}</span>
              <span>{t('tripDetail.travelers')}: {trip.travelers?.length || 0}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 3 main tabs - Simplified navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center gap-2 flex-wrap">
          <TabsList className="bg-[#F1F5F9] rounded-xl p-1 h-auto">
            {[
              { key: 'overview', icon: LayoutDashboard, label: isKidsMode ? '🏠 Accueil' : 'Tableau de bord', show: true },
              { key: 'carte', icon: MapPin, label: isKidsMode ? '🗺️ Carte' : 'Carte', show: true },
              { key: 'planning', icon: Calendar, label: isKidsMode ? '📅 Planning' : 'Planning', show: true },
              { key: 'logistics', icon: ClipboardList, label: 'Organisation', show: !isKidsMode && canEdit },
              { key: 'journal', icon: BookOpen, label: isKidsMode ? '📸 Mes Souvenirs' : 'Journal & Partage', show: true },
            ].filter(tab => tab.show).map(tab => (
              <TabsTrigger key={tab.key} value={tab.key} className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2 gap-1.5" data-testid={`tab-${tab.key}`}>
                <tab.icon className="w-3.5 h-3.5" strokeWidth={1.8} /> {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {trip.incidents?.some(i => i.status === 'open' || i.status === 'replanning') && !isKidsMode && (
            <button onClick={() => setActiveTab('planb')} className={`flex items-center gap-1.5 rounded-xl text-xs font-medium px-4 py-2 transition-all ${activeTab === 'planb' ? 'bg-red-500 text-white shadow-sm' : 'bg-white border border-red-200 text-red-500 hover:bg-red-50'}`} data-testid="tab-planb">
              <AlertTriangle className="w-3.5 h-3.5" /> Plan B
            </button>
          )}
        </div>

        <TabsContent value="overview" className="mt-4">
          <OverviewSection trip={trip} tripId={tripId} onRefresh={loadTrip} setActiveTab={setActiveTab} />
        </TabsContent>

        <TabsContent value="carte" className="mt-4">
          <TripMap tripId={tripId} trip={trip} />
        </TabsContent>

        <TabsContent value="planning" className="mt-4">
          <DayPlanner tripId={tripId} trip={trip} />
        </TabsContent>

        <TabsContent value="logistics" className="mt-4">
          <LogisticsSection trip={trip} tripId={tripId} families={families} onRefresh={loadTrip} />
        </TabsContent>

        <TabsContent value="journal" className="mt-4">
          <JournalSection tripId={tripId} trip={trip} />
        </TabsContent>

        <TabsContent value="planb" className="mt-4">
          <IncidentMode tripId={tripId} trip={trip} />
        </TabsContent>
      </Tabs>

      {/* QuickDeck FAB - Available everywhere */}
      <QuickDeckFAB onScanHere={() => setScanHereOpen(true)} />
      
      {/* Scan Here Modal */}
      <ScanHereModal open={scanHereOpen} onClose={() => setScanHereOpen(false)} />
    </div>
  );
}

/* ═══ SUB-SECTIONS ═══ */

function OverviewSection({ trip, tripId, onRefresh, setActiveTab }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  
  const tasksDone = trip.tasks?.filter(t => t.status === 'done').length || 0;
  const tasksTotal = trip.tasks?.length || 0;
  const compOk = trip.compliance?.filter(c => c.status === 'valid' || c.status === 'not_required').length || 0;
  const compTotal = trip.compliance?.length || 0;
  
  // Get next steps from planning
  const getDayPlans = () => {
    // Mock - In production, fetch from API
    const today = new Date();
    const startDate = new Date(trip.start_date);
    const endDate = new Date(trip.end_date);
    
    if (today < startDate) {
      // Before trip - show preparation tasks
      return {
        phase: 'preparation',
        daysUntil: Math.ceil((startDate - today) / 86400000),
        nextSteps: [
          { type: 'task', label: 'Terminer les réservations', count: trip.bookings?.filter(b => !b.confirmed).length || 0, action: () => setActiveTab('logistics') },
          { type: 'task', label: 'Valider les documents', count: trip.compliance?.filter(c => c.status !== 'valid').length || 0, action: () => setActiveTab('logistics') },
          { type: 'task', label: 'Tâches en attente', count: trip.tasks?.filter(t => t.status !== 'done').length || 0, action: () => setActiveTab('logistics') },
        ].filter(s => s.count > 0)
      };
    } else if (today >= startDate && today <= endDate) {
      // During trip - show today's activities
      return {
        phase: 'ongoing',
        currentDay: Math.floor((today - startDate) / 86400000) + 1,
        totalDays: Math.floor((endDate - startDate) / 86400000) + 1,
        nextSteps: [
          { type: 'activity', label: 'Voir le planning du jour', icon: Calendar, action: () => setActiveTab('planning') },
          { type: 'activity', label: 'Explorer la carte', icon: MapPin, action: () => setActiveTab('carte') },
        ]
      };
    } else {
      // After trip
      return {
        phase: 'completed',
        nextSteps: [
          { type: 'memory', label: 'Compléter le journal de voyage', icon: BookOpen, action: () => setActiveTab('journal') },
        ]
      };
    }
  };
  
  const whatsNext = getDayPlans();
  
  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: Users, label: t('tripDetail.travelers'), count: trip.travelers?.length || 0, color: '#3A86FF', bg: '#EBF2FF' },
          { icon: Plane, label: t('tripDetail.bookings'), count: trip.bookings?.length || 0, color: '#00CC99', bg: '#ECFDF5' },
          { icon: ClipboardList, label: t('tripDetail.tasks'), count: `${tasksDone}/${tasksTotal}`, color: '#F59E0B', bg: '#FFF8EB' },
          { icon: ShieldCheck, label: t('tripDetail.compliance'), count: `${compOk}/${compTotal}`, color: '#8B5CF6', bg: '#F5F3FF' },
        ].map((s, i) => (
          <div key={i} className="widget-card p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('logistics')}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: s.bg }}>
                <s.icon className="w-5 h-5" style={{ color: s.color }} strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-xl font-bold text-[#1E293B]">{s.count}</p>
                <p className="text-[10px] text-[#94A3B8] uppercase tracking-wider font-medium">{s.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* What's Next Section */}
      <Card className="border border-[#E2E8F0]/40 rounded-2xl overflow-hidden">
        <CardContent className="p-0">
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-5 border-b border-[#E2E8F0]/40">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm">
                <Sparkles className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-[#1E293B]">À venir</h3>
                <p className="text-xs text-[#64748B]">
                  {whatsNext.phase === 'preparation' && `Départ dans ${whatsNext.daysUntil} jours`}
                  {whatsNext.phase === 'ongoing' && `Jour ${whatsNext.currentDay}/${whatsNext.totalDays} du voyage`}
                  {whatsNext.phase === 'completed' && 'Voyage terminé'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="p-5 space-y-3">
            {whatsNext.nextSteps.length === 0 ? (
              <div className="text-center py-6">
                <Clock className="w-8 h-8 text-[#CBD5E1] mx-auto mb-2" />
                <p className="text-sm text-[#94A3B8]">Tout est prêt ! 🎉</p>
              </div>
            ) : (
              whatsNext.nextSteps.map((step, i) => {
                const StepIcon = step.icon || ChevronRight;
                return (
                  <button
                    key={i}
                    onClick={step.action}
                    className="w-full flex items-center justify-between p-4 rounded-xl bg-white border border-[#E2E8F0] hover:border-[#3A86FF]/30 hover:shadow-md transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#F8FAFC] flex items-center justify-center group-hover:bg-[#3A86FF]/10 transition-colors">
                        <StepIcon className="w-4 h-4 text-[#64748B] group-hover:text-[#3A86FF]" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-medium text-[#1E293B]">{step.label}</p>
                        {step.count !== undefined && (
                          <p className="text-xs text-[#64748B]">{step.count} élément(s)</p>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#94A3B8] group-hover:text-[#3A86FF] group-hover:translate-x-1 transition-all" />
                  </button>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* Budget Section */}
      <BudgetCharts tripId={tripId} trip={trip} />
    </div>
  );
}

function PlanningSection({ tripId, trip, onRefresh }) {
  const [sub, setSub] = useState('carte');
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {[{ key: 'carte', icon: MapPin, label: 'Carte' }, { key: 'planner', icon: Calendar, label: 'Planificateur' }].map(s => (
          <button key={s.key} onClick={() => setSub(s.key)} className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-medium transition-all ${sub === s.key ? 'bg-[#3A86FF] text-white shadow-sm' : 'bg-white border border-[#E2E8F0] text-[#64748B] hover:border-[#3A86FF]/30'}`} data-testid={`sub-${s.key}`}>
            <s.icon className="w-3.5 h-3.5" strokeWidth={1.8} /> {s.label}
          </button>
        ))}
      </div>
      {sub === 'carte' && <TripMap tripId={tripId} trip={trip} />}
      {sub === 'planner' && <DayPlanner tripId={tripId} trip={trip} />}
    </div>
  );
}

function LogisticsSection({ trip, tripId, families, onRefresh }) {
  const [sub, setSub] = useState('travelers');
  const [inviteOpen, setInviteOpen] = useState(false);
  
  return (
    <div className="space-y-4">
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {[
          { key: 'travelers', icon: Users, label: 'Voyageurs', count: trip.travelers?.length },
          { key: 'bookings', icon: Plane, label: 'Reservations', count: trip.bookings?.length },
          { key: 'tasks', icon: ClipboardList, label: 'Taches', count: trip.tasks?.length },
          { key: 'compliance', icon: ShieldCheck, label: 'Formalites', count: trip.compliance?.length },
          { key: 'health', icon: Heart, label: 'Sante' },
          { key: 'budget', icon: Wallet, label: 'Budget' },
        ].map(s => (
          <button key={s.key} onClick={() => setSub(s.key)} className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all whitespace-nowrap ${sub === s.key ? 'bg-[#3A86FF] text-white shadow-sm' : 'bg-white border border-[#E2E8F0] text-[#64748B] hover:border-[#3A86FF]/30'}`} data-testid={`sub-${s.key}`}>
            <s.icon className="w-3.5 h-3.5" strokeWidth={1.8} /> {s.label}
            {s.count !== undefined && <Badge className={`text-[9px] ml-0.5 rounded-md border-0 ${sub === s.key ? 'bg-white/20 text-white' : 'bg-[#F1F5F9] text-[#94A3B8]'}`}>{s.count || 0}</Badge>}
          </button>
        ))}
      </div>
      
      {/* Invite Traveler Modal */}
      <InviteTravelerModal 
        tripId={tripId} 
        open={inviteOpen} 
        onClose={() => setInviteOpen(false)} 
        onSuccess={onRefresh}
      />
      
      {sub === 'travelers' && <TravelersTab trip={trip} onRefresh={onRefresh} onInvite={() => setInviteOpen(true)} />}
      {sub === 'bookings' && <BookingsTab trip={trip} onRefresh={onRefresh} />}
      {sub === 'tasks' && <TasksTab trip={trip} families={families} onRefresh={onRefresh} />}
      {sub === 'compliance' && <ComplianceTab trip={trip} onRefresh={onRefresh} />}
      {sub === 'health' && <HealthModule tripId={tripId} trip={trip} />}
      {sub === 'budget' && <><BudgetCharts tripId={tripId} trip={trip} /><div className="mt-4"><BudgetTab trip={trip} onRefresh={onRefresh} /></div></>}
    </div>
  );
}

function JournalSection({ tripId, trip }) {
  const [sub, setSub] = useState('journal');
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {[{ key: 'journal', icon: BookOpen, label: 'Carnet de bord' }, { key: 'visits', icon: Compass, label: 'Visites & Quiz' }, { key: 'share', icon: Link2, label: 'Partage famille' }].map(s => (
          <button key={s.key} onClick={() => setSub(s.key)} className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-medium transition-all ${sub === s.key ? 'bg-[#3A86FF] text-white shadow-sm' : 'bg-white border border-[#E2E8F0] text-[#64748B] hover:border-[#3A86FF]/30'}`} data-testid={`sub-${s.key}`}>
            <s.icon className="w-3.5 h-3.5" strokeWidth={1.8} /> {s.label}
          </button>
        ))}
      </div>
      {sub === 'journal' && <JournalGallery tripId={tripId} />}
      {sub === 'visits' && <VisitCompanion tripId={tripId} trip={trip} />}
      {sub === 'share' && <ShareLinks tripId={tripId} />}
    </div>
  );
}

function TravelersTab({ trip, onRefresh }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', type: 'adult', age: '', nationality: '', passport_expiry: '' });
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingTraveler, setEditingTraveler] = useState(null);

  const handleAdd = async () => {
    if (!form.name) return;
    await addTraveler(trip.trip_id, { ...form, age: form.age ? parseInt(form.age) : null });
    setForm({ name: '', type: 'adult', age: '', nationality: '', passport_expiry: '' });
    setOpen(false);
    toast.success('Voyageur ajoute');
    onRefresh();
  };

  const handleRemove = async (id) => {
    await removeTraveler(trip.trip_id, id);
    toast.success('Voyageur supprime');
    onRefresh();
  };

  const handleEdit = (traveler) => {
    setEditingTraveler({ ...traveler });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingTraveler?.name) return;
    
    console.log('[TripDetail] Saving traveler:', editingTraveler);
    console.log('[TripDetail] traveler_id:', editingTraveler.traveler_id);
    
    try {
      const payload = {
        name: editingTraveler.name,
        type: editingTraveler.type,
        age: editingTraveler.age ? parseInt(editingTraveler.age) : null,
        nationality: editingTraveler.nationality,
        passport_expiry: editingTraveler.passport_expiry,
        email: editingTraveler.email || null
      };
      
      console.log('[TripDetail] Payload:', payload);
      
      await updateTraveler(editingTraveler.traveler_id, payload);
      setEditOpen(false);
      setEditingTraveler(null);
      toast.success('Voyageur mis à jour');
      onRefresh();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
      console.error('[TripDetail] Update error:', error);
    }
  };

  const handleSendInvitationFromEdit = async () => {
    console.log('🔔 [INVITATION] Button clicked!', editingTraveler);
    
    if (!editingTraveler?.email) {
      toast.error('Veuillez ajouter un email');
      console.log('❌ [INVITATION] No email');
      return;
    }

    try {
      // Determine role based on traveler type or default to visitor
      const role = editingTraveler.type === 'child' || editingTraveler.type === 'infant' ? 'kids' : 'visitor';
      
      console.log('📧 [INVITATION] Sending to:', editingTraveler.email, 'Role:', role, 'Trip:', trip.trip_id);
      
      await inviteTraveler(trip.trip_id, editingTraveler.email, editingTraveler.name, role);
      
      console.log('✅ [INVITATION] Sent successfully');
      toast.success(`✅ Invitation envoyée à ${editingTraveler.email}`);
    } catch (error) {
      console.error('❌ [INVITATION] Error:', error);
      toast.error('Erreur lors de l\'envoi de l\'invitation');
    }
  };

  const nationalities = [
    {code:'FR',flag:'🇫🇷',label:'Francaise'},{code:'BE',flag:'🇧🇪',label:'Belge'},{code:'CH',flag:'🇨🇭',label:'Suisse'},
    {code:'CA',flag:'🇨🇦',label:'Canadienne'},{code:'US',flag:'🇺🇸',label:'Americaine'},{code:'GB',flag:'🇬🇧',label:'Britannique'},
    {code:'DE',flag:'🇩🇪',label:'Allemande'},{code:'IT',flag:'🇮🇹',label:'Italienne'},{code:'ES',flag:'🇪🇸',label:'Espagnole'},
    {code:'PT',flag:'🇵🇹',label:'Portugaise'},{code:'NL',flag:'🇳🇱',label:'Neerlandaise'},{code:'LU',flag:'🇱🇺',label:'Luxembourgeoise'},
    {code:'IE',flag:'🇮🇪',label:'Irlandaise'},{code:'AT',flag:'🇦🇹',label:'Autrichienne'},{code:'SE',flag:'🇸🇪',label:'Suedoise'},
    {code:'DK',flag:'🇩🇰',label:'Danoise'},{code:'NO',flag:'🇳🇴',label:'Norvegienne'},{code:'FI',flag:'🇫🇮',label:'Finlandaise'},
    {code:'PL',flag:'🇵🇱',label:'Polonaise'},{code:'CZ',flag:'🇨🇿',label:'Tcheque'},{code:'GR',flag:'🇬🇷',label:'Grecque'},
    {code:'RO',flag:'🇷🇴',label:'Roumaine'},{code:'HU',flag:'🇭🇺',label:'Hongroise'},{code:'HR',flag:'🇭🇷',label:'Croate'},
    {code:'BG',flag:'🇧🇬',label:'Bulgare'},{code:'SK',flag:'🇸🇰',label:'Slovaque'},{code:'SI',flag:'🇸🇮',label:'Slovene'},
    {code:'EE',flag:'🇪🇪',label:'Estonienne'},{code:'LV',flag:'🇱🇻',label:'Lettone'},{code:'LT',flag:'🇱🇹',label:'Lituanienne'},
    {code:'MT',flag:'🇲🇹',label:'Maltaise'},{code:'CY',flag:'🇨🇾',label:'Chypriote'},
    {code:'JP',flag:'🇯🇵',label:'Japonaise'},{code:'KR',flag:'🇰🇷',label:'Coreenne'},{code:'CN',flag:'🇨🇳',label:'Chinoise'},
    {code:'IN',flag:'🇮🇳',label:'Indienne'},{code:'BR',flag:'🇧🇷',label:'Bresilienne'},{code:'MX',flag:'🇲🇽',label:'Mexicaine'},
    {code:'AR',flag:'🇦🇷',label:'Argentine'},{code:'AU',flag:'🇦🇺',label:'Australienne'},{code:'NZ',flag:'🇳🇿',label:'Neo-zelandaise'},
    {code:'ZA',flag:'🇿🇦',label:'Sud-africaine'},{code:'MA',flag:'🇲🇦',label:'Marocaine'},{code:'TN',flag:'🇹🇳',label:'Tunisienne'},
    {code:'DZ',flag:'🇩🇿',label:'Algerienne'},{code:'SN',flag:'🇸🇳',label:'Senegalaise'},{code:'CI',flag:'🇨🇮',label:'Ivoirienne'},
    {code:'TR',flag:'🇹🇷',label:'Turque'},{code:'RU',flag:'🇷🇺',label:'Russe'},{code:'UA',flag:'🇺🇦',label:'Ukrainienne'},
    {code:'IL',flag:'🇮🇱',label:'Israelienne'},{code:'LB',flag:'🇱🇧',label:'Libanaise'},{code:'AE',flag:'🇦🇪',label:'Emiratie'},
    {code:'SA',flag:'🇸🇦',label:'Saoudienne'},{code:'TH',flag:'🇹🇭',label:'Thailandaise'},{code:'VN',flag:'🇻🇳',label:'Vietnamienne'},
    {code:'PH',flag:'🇵🇭',label:'Philippine'},{code:'ID',flag:'🇮🇩',label:'Indonesienne'},{code:'SG',flag:'🇸🇬',label:'Singapourienne'},
    {code:'CO',flag:'🇨🇴',label:'Colombienne'},{code:'CL',flag:'🇨🇱',label:'Chilienne'},{code:'PE',flag:'🇵🇪',label:'Peruvienne'},
    {code:'XX',flag:'🌍',label:'Autre'},
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl bg-[#3A86FF] hover:bg-[#3A86FF]/90 text-white text-xs h-9" data-testid="add-traveler-btn">
              <Plus className="w-4 h-4 mr-1" /> {t('tripDetail.addTraveler')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('tripDetail.addTraveler')}</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-4">
              <div>
                <Label className="text-sm">Nom</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 h-11" data-testid="traveler-name" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-sm">Type</Label>
                  <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                    <SelectTrigger className="mt-1 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="adult">Adulte</SelectItem>
                      <SelectItem value="child">Enfant</SelectItem>
                      <SelectItem value="infant">Bebe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Age</Label>
                  <Input type="number" value={form.age} onChange={e => setForm({...form, age: e.target.value})} className="mt-1 h-11" data-testid="traveler-age" />
                </div>
                <div>
                  <Label className="text-sm">Nationalite</Label>
                  <Select value={form.nationality} onValueChange={v => setForm({...form, nationality: v})}>
                    <SelectTrigger className="mt-1 h-11" data-testid="traveler-nationality"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {nationalities.map(n => <SelectItem key={n.code} value={n.label}>{n.flag} {n.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-sm">Expiration passeport</Label>
                <Input type="date" value={form.passport_expiry} onChange={e => setForm({...form, passport_expiry: e.target.value})} className="mt-1 h-11" />
              </div>
              <Button onClick={handleAdd} className="w-full rounded-xl bg-[#3A86FF] hover:bg-[#3A86FF]/90 text-white" data-testid="save-traveler-btn">{t('common.add')}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {(!trip.travelers || trip.travelers.length === 0) ? (
        <p className="text-sm text-[#64748B] text-center py-8">{t('common.noData')}</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {trip.travelers.map(tv => (
            <Card key={tv.traveler_id} className="border border-[#E2E8F0]/40 rounded-xl">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-medium text-[#1E293B]">{tv.name}</p>
                  <p className="text-xs text-[#64748B]">
                    {tv.type}{tv.age ? ` · ${tv.age} ans` : ''}
                    {tv.nationality ? ` · ${nationalities.find(n=>n.label===tv.nationality)?.flag || '🌍'} ${tv.nationality}` : ''}
                    {tv.passport_expiry ? ` · Passeport: ${tv.passport_expiry}` : ''}
                  </p>
                  {tv.email && (
                    <p className="text-xs text-[#3A86FF] flex items-center gap-1 mt-1">
                      <Mail className="w-3 h-3" /> {tv.email}
                    </p>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(tv)} className="h-8 w-8 p-0 text-[#3A86FF] hover:text-[#2563EB]" title="Modifier">
                    <Edit3 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleRemove(tv.traveler_id)} className="h-8 w-8 p-0 text-red-400 hover:text-red-600" data-testid={`remove-traveler-${tv.traveler_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Traveler Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-[#3A86FF]" />
              Modifier le voyageur
            </DialogTitle>
          </DialogHeader>
          {editingTraveler && (
            <div className="space-y-3 mt-4">
              <div>
                <Label className="text-sm">Nom</Label>
                <Input 
                  value={editingTraveler.name || ''} 
                  onChange={e => setEditingTraveler({...editingTraveler, name: e.target.value})} 
                  className="mt-1 h-11" 
                />
              </div>
              <div>
                <Label className="text-sm">Email (pour invitation)</Label>
                <div className="flex gap-2">
                  <Input 
                    type="email"
                    value={editingTraveler.email || ''} 
                    onChange={e => setEditingTraveler({...editingTraveler, email: e.target.value})} 
                    placeholder="exemple@email.com"
                    className="mt-1 h-11 flex-1" 
                  />
                  <Button 
                    variant="outline" 
                    onClick={handleSendInvitationFromEdit}
                    disabled={!editingTraveler.email}
                    className="mt-1 h-11"
                    title="Envoyer invitation maintenant"
                  >
                    <Mail className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-[#64748B] mt-1">
                  💡 Ajoutez un email pour envoyer une invitation à rejoindre l'app
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-sm">Type</Label>
                  <Select value={editingTraveler.type || 'adult'} onValueChange={v => setEditingTraveler({...editingTraveler, type: v})}>
                    <SelectTrigger className="mt-1 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="adult">Adulte</SelectItem>
                      <SelectItem value="child">Enfant</SelectItem>
                      <SelectItem value="infant">Bebe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Age</Label>
                  <Input 
                    type="number" 
                    value={editingTraveler.age || ''} 
                    onChange={e => setEditingTraveler({...editingTraveler, age: e.target.value})} 
                    className="mt-1 h-11" 
                  />
                </div>
                <div>
                  <Label className="text-sm">Nationalite</Label>
                  <Select value={editingTraveler.nationality || ''} onValueChange={v => setEditingTraveler({...editingTraveler, nationality: v})}>
                    <SelectTrigger className="mt-1 h-11"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {nationalities.map(n => <SelectItem key={n.code} value={n.label}>{n.flag} {n.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-sm">Expiration passeport</Label>
                <Input 
                  type="date" 
                  value={editingTraveler.passport_expiry || ''} 
                  onChange={e => setEditingTraveler({...editingTraveler, passport_expiry: e.target.value})} 
                  className="mt-1 h-11" 
                />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setEditOpen(false)}>
                  Annuler
                </Button>
                <Button onClick={handleSaveEdit} className="bg-[#3A86FF] hover:bg-[#3A86FF]/90 text-white">
                  Enregistrer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BookingsTab({ trip, onRefresh }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ type: 'flight', title: '', provider: '', confirmation_number: '', start_datetime: '', end_datetime: '' });
  const [open, setOpen] = useState(false);
  const fileRef = { current: null };

  const handleAdd = async () => {
    if (!form.title) return;
    await addBooking(trip.trip_id, form);
    setForm({ type: 'flight', title: '', provider: '', confirmation_number: '', start_datetime: '', end_datetime: '' });
    setOpen(false);
    toast.success('Booking added');
    onRefresh();
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await importBookings(trip.trip_id, file);
      toast.success(`${res.data.imported_count} bookings imported`);
      onRefresh();
    } catch { toast.error('Import failed'); }
  };

  const handleLock = async (id) => {
    await toggleLockBooking(id);
    onRefresh();
  };

  const typeIcons = { flight: Plane, hotel: '🏨', transport: '🚗', activity: '🎯', restaurant: '🍽️' };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 justify-end">
        <label>
          <input type="file" accept=".csv,.txt" className="hidden" onChange={handleImport} />
          <Button variant="outline" className="rounded-full" asChild data-testid="import-bookings-btn">
            <span><Upload className="w-4 h-4 mr-1" /> {t('tripDetail.importBookings')}</span>
          </Button>
        </label>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-[#3A86FF] hover:bg-[#3A86FF]/90 text-white" data-testid="add-booking-btn">
              <Plus className="w-4 h-4 mr-1" /> {t('tripDetail.addBooking')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('tripDetail.addBooking')}</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-4">
              <div>
                <Label className="text-sm">Type</Label>
                <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                  <SelectTrigger className="mt-1 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['flight','hotel','transport','activity','restaurant','other'].map(tp => (
                      <SelectItem key={tp} value={tp}>{t(`booking.${tp}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Title</Label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-1 h-11" data-testid="booking-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Provider</Label>
                  <Input value={form.provider} onChange={e => setForm({...form, provider: e.target.value})} className="mt-1 h-11" />
                </div>
                <div>
                  <Label className="text-sm">Confirmation #</Label>
                  <Input value={form.confirmation_number} onChange={e => setForm({...form, confirmation_number: e.target.value})} className="mt-1 h-11" />
                </div>
              </div>
              <Button onClick={handleAdd} className="w-full rounded-full bg-[#3A86FF] hover:bg-[#3A86FF]/90 text-white" data-testid="save-booking-btn">{t('common.add')}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {(!trip.bookings || trip.bookings.length === 0) ? (
        <p className="text-sm text-[#64748B] text-center py-8">{t('common.noData')}</p>
      ) : (
        <div className="space-y-2">
          {trip.bookings.map(bk => (
            <Card key={bk.booking_id} className="border border-[#E2E8F0]/40 rounded-xl" data-testid={`booking-${bk.booking_id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#3A86FF]/10 flex items-center justify-center text-lg">
                  {bk.type === 'flight' ? <Plane className="w-5 h-5 text-[#3A86FF]" /> : bk.type === 'hotel' ? '🏨' : bk.type === 'transport' ? '🚗' : '🎯'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#1E293B] truncate">{bk.title}</p>
                  <p className="text-xs text-[#64748B]">
                    {bk.provider && `${bk.provider} · `}{bk.confirmation_number && `#${bk.confirmation_number}`}
                    {bk.import_source !== 'manual' && <Badge variant="outline" className="ml-2 text-[10px]">{bk.import_source}</Badge>}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleLock(bk.booking_id)} className="h-8 w-8 p-0" data-testid={`lock-booking-${bk.booking_id}`}>
                  {bk.is_locked ? <Lock className="w-4 h-4 text-[#3A86FF]" /> : <Unlock className="w-4 h-4 text-[#64748B]" />}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function TasksTab({ trip, families, onRefresh }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ title: '', priority: 'medium', due_date: '' });
  const [open, setOpen] = useState(false);

  const handleAdd = async () => {
    if (!form.title) return;
    await createTask({ ...form, trip_id: trip.trip_id, family_id: families[0]?.family_id, status: 'todo' });
    setForm({ title: '', priority: 'medium', due_date: '' });
    setOpen(false);
    toast.success('Task added');
    onRefresh();
  };

  const handleStatusChange = async (taskId, status) => {
    await updateTask(taskId, { status });
    onRefresh();
  };

  const statuses = ['todo', 'in_progress', 'done'];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-[#3A86FF] hover:bg-[#3A86FF]/90 text-white" data-testid="add-task-btn">
              <Plus className="w-4 h-4 mr-1" /> {t('tripDetail.addTask')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('tripDetail.addTask')}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-sm">Title</Label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-1 h-11" data-testid="task-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">{t('tasks.priority')}</Label>
                  <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
                    <SelectTrigger className="mt-1 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">{t('tasks.high')}</SelectItem>
                      <SelectItem value="medium">{t('tasks.medium')}</SelectItem>
                      <SelectItem value="low">{t('tasks.low')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">{t('tasks.dueDate')}</Label>
                  <Input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} className="mt-1 h-11" />
                </div>
              </div>
              <Button onClick={handleAdd} className="w-full rounded-full bg-[#3A86FF] hover:bg-[#3A86FF]/90 text-white" data-testid="save-task-btn">{t('common.add')}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {/* Kanban-style columns */}
      <div className="grid md:grid-cols-3 gap-4">
        {statuses.map(status => {
          const statusTasks = (trip.tasks || []).filter(t => t.status === status);
          return (
            <div key={status} className="space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-2 h-2 rounded-full ${status === 'todo' ? 'bg-blue-500' : status === 'in_progress' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                <span className="text-sm font-medium text-[#1E293B]">{t(`tasks.${status === 'in_progress' ? 'inProgress' : status}`)}</span>
                <Badge variant="secondary" className="text-[10px] rounded-full">{statusTasks.length}</Badge>
              </div>
              <div className="space-y-2 min-h-[100px] bg-[#F1F5F9]/50 rounded-xl p-2">
                {statusTasks.map(task => (
                  <Card key={task.task_id} className="border border-[#E2E8F0]/40 rounded-lg" data-testid={`task-card-${task.task_id}`}>
                    <CardContent className="p-3">
                      <p className="text-sm text-[#1E293B] mb-2">{task.title}</p>
                      <div className="flex items-center justify-between">
                        <div className={`w-1.5 h-1.5 rounded-full ${task.priority === 'high' ? 'bg-red-500' : task.priority === 'medium' ? 'bg-amber-500' : 'bg-blue-500'}`} />
                        <Select value={task.status} onValueChange={v => handleStatusChange(task.task_id, v)}>
                          <SelectTrigger className="h-7 text-[10px] w-24 border-0 bg-[#F1F5F9]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="todo">{t('tasks.todo')}</SelectItem>
                            <SelectItem value="in_progress">{t('tasks.inProgress')}</SelectItem>
                            <SelectItem value="done">{t('tasks.done')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ComplianceTab({ trip, onRefresh }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ traveler_id: '', type: 'passport', title: '', severity: 'important', deadline: '' });
  const [open, setOpen] = useState(false);
  const [aiChecking, setAiChecking] = useState(false);

  const handleAdd = async () => {
    if (!form.title || !form.traveler_id) return;
    await addCompliance(trip.trip_id, form);
    setForm({ traveler_id: '', type: 'passport', title: '', severity: 'important', deadline: '' });
    setOpen(false);
    toast.success('Item added');
    onRefresh();
  };

  const handleAiCheck = async () => {
    if (!trip.travelers?.length) { toast.error('Ajoutez des voyageurs d\'abord'); return; }
    setAiChecking(true);
    try {
      const { aiComplianceCheck } = await import('@/lib/api');
      const res = await aiComplianceCheck(trip.trip_id);
      toast.success(`${res.data.generated_count} formalites generees par l'IA`);
      onRefresh();
    } catch (e) {
      toast.error('Erreur: ' + (e.response?.data?.detail || 'Echec'));
    }
    setAiChecking(false);
  };

  const handleStatusUpdate = async (itemId, status) => {
    await updateCompliance(itemId, { status });
    onRefresh();
  };

  const severityStyles = {
    blocking: 'bg-red-100 text-red-800 border-red-200',
    important: 'bg-amber-100 text-amber-800 border-amber-200',
    optional: 'bg-blue-100 text-blue-800 border-blue-200',
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 justify-end">
        <Button
          onClick={handleAiCheck}
          disabled={aiChecking}
          className="rounded-xl bg-gradient-to-r from-[#3A86FF] to-[#7C3AED] text-white text-xs h-9 px-4"
          data-testid="ai-compliance-check-btn"
        >
          {aiChecking ? (
            <><span className="w-3.5 h-3.5 mr-1.5 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block" /> Analyse IA en cours...</>
          ) : (
            <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Verifier par IA</>
          )}
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl bg-[#3A86FF] hover:bg-[#3A86FF]/90 text-white text-xs h-9" data-testid="add-compliance-btn">
              <Plus className="w-4 h-4 mr-1" /> {t('tripDetail.addCompliance')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('tripDetail.addCompliance')}</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-4">
              <div>
                <Label className="text-sm">Traveler</Label>
                <Select value={form.traveler_id} onValueChange={v => setForm({...form, traveler_id: v})}>
                  <SelectTrigger className="mt-1 h-11"><SelectValue placeholder="Select traveler" /></SelectTrigger>
                  <SelectContent>
                    {(trip.travelers || []).map(tv => (
                      <SelectItem key={tv.traveler_id} value={tv.traveler_id}>{tv.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Type</Label>
                  <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                    <SelectTrigger className="mt-1 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['passport','visa','insurance','minor_auth','health','other'].map(tp => (
                        <SelectItem key={tp} value={tp}>{t(`compliance.${tp === 'minor_auth' ? 'minorAuth' : tp}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">{t('compliance.blocking')}/{t('compliance.important')}</Label>
                  <Select value={form.severity} onValueChange={v => setForm({...form, severity: v})}>
                    <SelectTrigger className="mt-1 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="blocking">{t('compliance.blocking')}</SelectItem>
                      <SelectItem value="important">{t('compliance.important')}</SelectItem>
                      <SelectItem value="optional">{t('compliance.optional')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-sm">Title</Label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-1 h-11" data-testid="compliance-title" />
              </div>
              <div>
                <Label className="text-sm">Deadline</Label>
                <Input type="date" value={form.deadline} onChange={e => setForm({...form, deadline: e.target.value})} className="mt-1 h-11" />
              </div>
              <Button onClick={handleAdd} className="w-full rounded-full bg-[#3A86FF] hover:bg-[#3A86FF]/90 text-white" data-testid="save-compliance-btn">{t('common.add')}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {(!trip.compliance || trip.compliance.length === 0) ? (
        <p className="text-sm text-[#64748B] text-center py-8">{t('common.noData')}</p>
      ) : (
        <div className="space-y-2">
          {trip.compliance.map(item => {
            const traveler = trip.travelers?.find(tv => tv.traveler_id === item.traveler_id);
            return (
              <Card key={item.item_id} className="border border-[#E2E8F0]/40 rounded-xl" data-testid={`compliance-${item.item_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <Badge className={`text-[10px] rounded-full border ${severityStyles[item.severity]}`}>{t(`compliance.${item.severity}`)}</Badge>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm text-[#1E293B]">{item.title}</p>
                        {item.ai_generated && <Badge className="text-[8px] bg-[#EBF2FF] text-[#3A86FF] border-0 rounded-md px-1.5">IA</Badge>}
                      </div>
                      <p className="text-xs text-[#64748B]">
                        {traveler?.name || '?'} · {t(`compliance.${item.type === 'minor_auth' ? 'minorAuth' : item.type}`)}
                        {item.deadline && <span className="ml-2 text-[#3A86FF]">Avant le {new Date(item.deadline).toLocaleDateString()}</span>}
                      </p>
                    </div>
                    <Select value={item.status} onValueChange={v => handleStatusUpdate(item.item_id, v)}>
                      <SelectTrigger className="h-8 text-xs w-28 border-[#E2E8F0]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['pending','valid','expiring','expired'].map(s => (
                          <SelectItem key={s} value={s}>{t(`compliance.${s}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {item.notes && <p className="text-[11px] text-[#94A3B8] mt-2 ml-[60px] leading-relaxed">{item.notes}</p>}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BudgetTab({ trip, onRefresh }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ date: '', category: 'other', title: '', amount: '', is_planned: false });
  const [open, setOpen] = useState(false);

  const handleAdd = async () => {
    if (!form.title || !form.amount) return;
    await addExpense(trip.trip_id, { ...form, amount: parseFloat(form.amount), currency: trip.currency });
    setForm({ date: '', category: 'other', title: '', amount: '', is_planned: false });
    setOpen(false);
    toast.success('Expense added');
    onRefresh();
  };

  const planned = (trip.expenses || []).filter(e => e.is_planned);
  const actual = (trip.expenses || []).filter(e => !e.is_planned);
  const totalPlanned = planned.reduce((a, e) => a + (e.amount || 0), 0);
  const totalActual = actual.reduce((a, e) => a + (e.amount || 0), 0);

  const catColors = { hotel: '#3A86FF', flight: '#3A86FF', transport: '#00CC99', food: '#F59E0B', activity: '#8B5CF6', shopping: '#F59E0B', other: '#64748B' };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-[#3A86FF] hover:bg-[#3A86FF]/90 text-white" data-testid="add-expense-btn">
              <Plus className="w-4 h-4 mr-1" /> {t('tripDetail.addExpense')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('tripDetail.addExpense')}</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-4">
              <div>
                <Label className="text-sm">Title</Label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-1 h-11" data-testid="expense-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">{t('budget.total')}</Label>
                  <Input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} className="mt-1 h-11" data-testid="expense-amount" />
                </div>
                <div>
                  <Label className="text-sm">Date</Label>
                  <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="mt-1 h-11" />
                </div>
              </div>
              <div>
                <Label className="text-sm">Category</Label>
                <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                  <SelectTrigger className="mt-1 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['hotel','flight','transport','food','activity','shopping','other'].map(c => (
                      <SelectItem key={c} value={c}>{t(`budget.${c}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="is_planned" checked={form.is_planned} onChange={e => setForm({...form, is_planned: e.target.checked})} className="rounded" />
                <Label htmlFor="is_planned" className="text-sm">{t('budget.planned')}</Label>
              </div>
              <Button onClick={handleAdd} className="w-full rounded-full bg-[#3A86FF] hover:bg-[#3A86FF]/90 text-white" data-testid="save-expense-btn">{t('common.add')}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Card className="border border-[#E2E8F0]/40 rounded-2xl">
          <CardContent className="p-5 text-center">
            <p className="text-xs text-[#64748B] uppercase tracking-wide mb-1">{t('budget.planned')}</p>
            <p className="text-2xl font-bold text-[#3A86FF]">{totalPlanned.toFixed(2)} {trip.currency}</p>
          </CardContent>
        </Card>
        <Card className="border border-[#E2E8F0]/40 rounded-2xl">
          <CardContent className="p-5 text-center">
            <p className="text-xs text-[#64748B] uppercase tracking-wide mb-1">{t('budget.actual')}</p>
            <p className="text-2xl font-bold text-[#3A86FF]">{totalActual.toFixed(2)} {trip.currency}</p>
          </CardContent>
        </Card>
        <Card className="border border-[#E2E8F0]/40 rounded-2xl">
          <CardContent className="p-5 text-center">
            <p className="text-xs text-[#64748B] uppercase tracking-wide mb-1">{totalActual > totalPlanned ? 'Over' : 'Under'}</p>
            <p className={`text-2xl font-bold ${totalActual > totalPlanned ? 'text-red-500' : 'text-[#00CC99]'}`}>
              {Math.abs(totalActual - totalPlanned).toFixed(2)} {trip.currency}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Expense list */}
      {(trip.expenses || []).length > 0 && (
        <div className="space-y-2">
          {trip.expenses.map(exp => (
            <Card key={exp.expense_id} className="border border-[#E2E8F0]/40 rounded-xl">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-2 h-8 rounded-full" style={{ backgroundColor: catColors[exp.category] || '#64748B' }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1E293B]">{exp.title}</p>
                  <p className="text-xs text-[#64748B]">{t(`budget.${exp.category}`)} · {exp.date}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-[#1E293B]">{exp.amount} {exp.currency}</p>
                  {exp.is_planned && <Badge variant="secondary" className="text-[9px] rounded-full">{t('budget.planned')}</Badge>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
